import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import ConfirmDialog from '../../components/ConfirmDialog';

const EMP_TYPES = { full_time:'Full Time', part_time:'Part Time', probation:'Probation', intern:'Intern', freelancer:'Freelancer', contract:'Contract', consultant:'Consultant', temporary:'Temporary', remote:'Remote', hybrid:'Hybrid' };
const TYPE_COLORS = { full_time:'pill-blue', part_time:'pill-purple', probation:'pill-orange', intern:'pill-green', freelancer:'pill-orange', contract:'pill-gray', consultant:'pill-gray', temporary:'pill-gray', remote:'pill-blue', hybrid:'pill-purple' };

const DEPT_DESIGNATIONS = {
  'Management':            ['Founder','Co-Founder','CEO','Managing Director','Director','Chief Operating Officer (COO)','General Manager','Operations Manager','Branch Manager'],
  'Project Management':    ['Project Manager','Senior Project Manager','Account Manager','Client Success Manager','Project Coordinator','Team Leader'],
  'Creative':              ['Design Intern','Junior Graphic Designer','Graphic Designer','Senior Graphic Designer','Lead Graphic Designer','Creative Lead','Creative Director','Brand Strategist','Senior Brand Strategist','Brand Consultant','UI Designer','UX Designer','UI/UX Designer','Senior UI/UX Designer','Motion Graphics Intern','Motion Graphics Designer','Senior Motion Graphics Designer','Video Editor','Senior Video Editor','Video Producer','Illustrator','Digital Artist'],
  'Marketing':             ['Marketing Intern','Marketing Executive','Digital Marketing Executive','SEO Executive','Performance Marketing Executive','Content Writer','Copywriter','Social Media Executive','Social Media Manager','Marketing Manager','Marketing Head'],
  'Sales':                 ['Sales Intern','Sales Executive','Business Development Executive (BDE)','Senior Sales Executive','Business Development Manager (BDM)','Sales Manager','Sales Head','Key Account Manager'],
  'Client Relations':      ['Client Coordinator','Client Relationship Executive','Client Relationship Manager','Key Account Manager','Customer Success Executive'],
  'Accounts & Finance':    ['Accounts Intern','Accounts Executive','Accounts Officer','Senior Accountant','Finance Executive','Finance Manager','Accounts Manager','Chief Financial Officer'],
  'HR':                    ['HR Intern','HR Executive','Senior HR Executive','HR Manager','HR Business Partner','Talent Acquisition Executive','Recruitment Specialist'],
  'Administration':        ['Office Assistant','Receptionist','Office Administrator','Office Manager','Admin Executive'],
  'IT':                    ['IT Support Executive','System Administrator','Network Administrator','DevOps Engineer','IT Manager'],
  'Development':           ['Software Intern','Frontend Developer','Backend Developer','Full Stack Developer','Senior Developer','Technical Lead','Solution Architect','QA Engineer','Product Manager'],
  'Media Production':      ['Photographer','Videographer','Drone Operator','Studio Assistant','Production Manager'],
  'Printing & Production': ['Print Executive','Production Executive','Vendor Coordinator'],
  'Legal':                 ['Legal Executive','Legal Advisor','Compliance Officer'],
};
const ALL_DEPARTMENTS = Object.keys(DEPT_DESIGNATIONS);
const EMP_STATUS = { active: 'Active', probation: 'Probation', notice_period: 'Notice Period', resigned: 'Resigned', terminated: 'Terminated' };
const STATUS_COLORS = { active: 'pill-green', probation: 'pill-orange', notice_period: 'pill-orange', resigned: 'pill-gray', terminated: 'pill-danger' };

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const EMPTY_FORM = {
  employee_code: '', full_name: '', designation: '', department: '',
  joining_date: '', employment_type: 'full_time', employment_status: 'active',
  salary_structure_id: '',
  // personal
  date_of_birth: '', gender: '', blood_group: '', marital_status: '',
  mobile_number: '', personal_email: '', company_email: '',
  emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '',
  // employment extras
  confirmation_date: '', exit_date: '', reporting_manager: '', work_location: '',
  // bank & IDs
  pan_number: '', aadhaar_number: '', uan_number: '',
  bank_name: '', account_number: '', ifsc_code: '',
  account_holder_name: '', bank_branch: '', upi_id: '',
  esic_number: '', professional_tax_number: '',
};

const EMPTY_GRANT = { username: '', password: '', role: 'team' };
const TABS = ['personal', 'employment', 'salary', 'bank', 'documents'];
const TAB_LABELS = { personal: 'Personal', employment: 'Employment', salary: 'Salary', bank: 'Bank & IDs', documents: 'Documents' };

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000';

function fileUrl(filename) {
  return filename ? `${API_BASE}/uploads/employees/${filename}` : null;
}

function DocUploadBox({ label, fieldKey, empId, currentFile, onUploaded, onDeleted, disabled }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);

  async function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/hr/employees/${empId}/upload?type=${fieldKey}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded(res.data.url, res.data.filename);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  async function handleDelete() {
    try {
      await api.delete(`/hr/employees/${empId}/upload?type=${fieldKey}`);
      onDeleted();
      toast.success(`${label} removed`);
    } catch (err) {
      toast.error('Delete failed');
    }
  }

  const url = fileUrl(currentFile);
  const isImage = currentFile && !currentFile.endsWith('.pdf');

  return (
    <div style={{ border: '1px dashed var(--border)', borderRadius: 8, padding: 14, background: 'var(--bg2)' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 8 }}>{label}</div>
      {url ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {isImage ? (
            <img src={url} alt={label} style={{ maxWidth: '100%', maxHeight: 140, objectFit: 'contain', borderRadius: 6, border: '1px solid var(--border)' }} />
          ) : (
            <a href={url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--accent)' }}>📄 View PDF</a>
          )}
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" className="btn btn-sm" onClick={() => inputRef.current?.click()} disabled={disabled || uploading}>Replace</button>
            <button type="button" className="btn-danger btn-sm" onClick={handleDelete} disabled={disabled}>Remove</button>
          </div>
        </div>
      ) : (
        <button type="button" className="btn btn-sm" onClick={() => inputRef.current?.click()} disabled={disabled || uploading} style={{ width: '100%' }}>
          {uploading ? 'Uploading…' : `+ Upload ${label}`}
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={handleFile} />
    </div>
  );
}

export default function EmployeesPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editEmp, setEditEmp] = useState(null); // full emp object for file display
  const [form, setForm] = useState(EMPTY_FORM);
  const [tab, setTab] = useState('personal');
  const [grantEmployee, setGrantEmployee] = useState(null);
  const [grantForm, setGrantForm] = useState(EMPTY_GRANT);
  const [revokeEmployee, setRevokeEmployee] = useState(null);
  const [deactivateEmployee, setDeactivateEmployee] = useState(null);

  // Track uploaded doc filenames for current edit session
  const [docFiles, setDocFiles] = useState({ profile_photo: null, aadhaar_doc: null, pan_doc: null, driving_license_doc: null });

  const profileInputRef = useRef();
  const [profileUploading, setProfileUploading] = useState(false);

  const { data: employees = [], isLoading } = useQuery({
    queryKey: ['hr-employees'],
    queryFn: () => api.get('/hr/employees').then(r => r.data),
  });
  const { data: salaryStructures = [] } = useQuery({
    queryKey: ['hr-salary'],
    queryFn: () => api.get('/hr/salary').then(r => r.data),
  });

  const departments = [...new Set(employees.map(e => e.department).filter(Boolean))].sort();
  const filtered = employees.filter(e => {
    const q = search.toLowerCase();
    const matchSearch = !q || e.full_name?.toLowerCase().includes(q) || e.employee_code?.toLowerCase().includes(q) || e.designation?.toLowerCase().includes(q);
    const matchDept = !deptFilter || e.department === deptFilter;
    return matchSearch && matchDept;
  });

  const saveMutation = useMutation({
    mutationFn: data => editId ? api.patch(`/hr/employees/${editId}`, data) : api.post('/hr/employees', data),
    onSuccess: () => {
      qc.invalidateQueries(['hr-employees']);
      qc.invalidateQueries(['users']);
      toast.success(editId ? 'Employee updated' : 'Employee added');
      setShowModal(false);
    },
    onError: err => toast.error(err.response?.data?.error || 'Save failed'),
  });

  const deactivateMutation = useMutation({
    mutationFn: id => api.delete(`/hr/employees/${id}`),
    onSuccess: () => { qc.invalidateQueries(['hr-employees']); toast.success('Employee deactivated'); setDeactivateEmployee(null); },
    onError: err => toast.error(err.response?.data?.error || 'Failed'),
  });

  const grantMutation = useMutation({
    mutationFn: ({ empId, ...data }) => api.post(`/hr/employees/${empId}/grant-access`, data),
    onSuccess: () => {
      qc.invalidateQueries(['hr-employees']);
      qc.invalidateQueries(['users']);
      toast.success('App access granted');
      setGrantEmployee(null);
      setGrantForm(EMPTY_GRANT);
    },
    onError: err => toast.error(err.response?.data?.error || 'Failed to grant access'),
  });

  const revokeMutation = useMutation({
    mutationFn: empId => api.delete(`/hr/employees/${empId}/revoke-access`),
    onSuccess: () => {
      qc.invalidateQueries(['hr-employees']);
      qc.invalidateQueries(['users']);
      toast.success('App access revoked');
      setRevokeEmployee(null);
    },
    onError: err => toast.error(err.response?.data?.error || 'Failed to revoke access'),
  });

  function openAdd() {
    setEditId(null);
    setEditEmp(null);
    setForm(EMPTY_FORM);
    setDocFiles({ profile_photo: null, aadhaar_doc: null, pan_doc: null, driving_license_doc: null });
    setTab('personal');
    setShowModal(true);
  }

  function openEdit(emp) {
    setEditId(emp.id);
    setEditEmp(emp);
    setDocFiles({
      profile_photo: emp.profile_photo || null,
      aadhaar_doc: emp.aadhaar_doc || null,
      pan_doc: emp.pan_doc || null,
      driving_license_doc: emp.driving_license_doc || null,
    });
    setForm({
      employee_code: emp.employee_code || '', full_name: emp.full_name || '',
      designation: emp.designation || '', department: emp.department || '',
      joining_date: emp.joining_date?.slice(0, 10) || '',
      employment_type: emp.employment_type || 'full_time',
      employment_status: emp.employment_status || 'active',
      salary_structure_id: emp.salary_structure_id || '',
      date_of_birth: emp.date_of_birth?.slice(0, 10) || '',
      gender: emp.gender || '', blood_group: emp.blood_group || '',
      marital_status: emp.marital_status || '', mobile_number: emp.mobile_number || '',
      personal_email: emp.personal_email || '', company_email: emp.company_email || '',
      emergency_contact_name: emp.emergency_contact_name || '',
      emergency_contact_phone: emp.emergency_contact_phone || '',
      emergency_contact_relationship: emp.emergency_contact_relationship || '',
      confirmation_date: emp.confirmation_date?.slice(0, 10) || '',
      exit_date: emp.exit_date?.slice(0, 10) || '',
      reporting_manager: emp.reporting_manager || '', work_location: emp.work_location || '',
      pan_number: emp.pan_number || '', aadhaar_number: emp.aadhaar_number || '',
      uan_number: emp.uan_number || '', bank_name: emp.bank_name || '',
      account_number: emp.account_number || '', ifsc_code: emp.ifsc_code || '',
      account_holder_name: emp.account_holder_name || '', bank_branch: emp.bank_branch || '',
      upi_id: emp.upi_id || '', esic_number: emp.esic_number || '',
      professional_tax_number: emp.professional_tax_number || '',
    });
    setTab('personal');
    setShowModal(true);
  }

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleProfileUpload(e) {
    if (!editId) { toast.error('Save employee first, then upload photo'); return; }
    const file = e.target.files[0];
    if (!file) return;
    setProfileUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/hr/employees/${editId}/upload?type=profile`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDocFiles(d => ({ ...d, profile_photo: res.data.filename }));
      qc.invalidateQueries(['hr-employees']);
      toast.success('Profile photo updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setProfileUploading(false);
      e.target.value = '';
    }
  }

  if (isLoading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div>
      <div className="section-head">
        <h2>Employees ({filtered.length})</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, code..." style={{ width: 200 }} />
          <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)} style={{ width: 180 }}>
            <option value="">All Departments</option>
            {ALL_DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <button className="btn-primary" onClick={openAdd}>+ Add Employee</button>
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>Code</th><th>Name</th><th>Designation</th><th>Department</th>
                <th>Status</th><th>Type</th><th>Joined</th><th>Salary Structure</th><th>App Access</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => (
                <tr key={e.id}>
                  <td>
                    {e.profile_photo
                      ? <img src={fileUrl(e.profile_photo)} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', border: '1px solid var(--border)' }} />
                      : <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>
                          {e.full_name?.[0]?.toUpperCase() || '?'}
                        </div>
                    }
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{e.employee_code}</td>
                  <td style={{ fontWeight: 600 }}>{e.full_name}</td>
                  <td style={{ color: 'var(--text2)' }}>{e.designation || '—'}</td>
                  <td>{e.department || '—'}</td>
                  <td><span className={`pill ${STATUS_COLORS[e.employment_status] || 'pill-gray'}`}>{EMP_STATUS[e.employment_status] || e.employment_status || 'Active'}</span></td>
                  <td><span className={`pill ${TYPE_COLORS[e.employment_type] || 'pill-gray'}`}>{EMP_TYPES[e.employment_type] || e.employment_type}</span></td>
                  <td style={{ color: 'var(--text2)' }}>{e.joining_date ? new Date(e.joining_date).toLocaleDateString('en-IN') : '—'}</td>
                  <td style={{ color: 'var(--text2)', fontSize: 12 }}>{e.salary_structure_name || <span style={{ color: 'var(--orange)' }}>Not set</span>}</td>
                  <td style={{ fontSize: 12 }}>
                    {e.username
                      ? <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontFamily: 'monospace', color: 'var(--accent)' }}>{e.username}</span>
                          <button className="btn-sm btn-danger" style={{ fontSize: 10, padding: '1px 6px' }} onClick={() => setRevokeEmployee(e)}>Revoke</button>
                        </span>
                      : <button className="btn-sm" style={{ fontSize: 11 }} onClick={() => { setGrantEmployee(e); setGrantForm(EMPTY_GRANT); }}>+ Grant Access</button>
                    }
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-sm" onClick={() => openEdit(e)}>✎ Edit</button>
                      <button className="btn-danger btn-sm" onClick={() => setDeactivateEmployee(e)}>✕</button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={11} className="empty">No employees found</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit Employee modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 680, width: '95vw' }}>
            <div className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              {/* Profile photo */}
              <div style={{ position: 'relative', flexShrink: 0 }}>
                {docFiles.profile_photo
                  ? <img src={fileUrl(docFiles.profile_photo)} alt="Profile" style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--accent)' }} />
                  : <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700, color: 'var(--accent)' }}>
                      {form.full_name?.[0]?.toUpperCase() || '?'}
                    </div>
                }
                <button type="button" title="Change photo"
                  onClick={() => editId ? profileInputRef.current?.click() : toast('Save employee first, then add photo')}
                  disabled={profileUploading}
                  style={{ position: 'absolute', bottom: 0, right: 0, width: 18, height: 18, borderRadius: '50%', background: 'var(--accent)', border: 'none', cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  ✎
                </button>
                <input ref={profileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleProfileUpload} />
              </div>
              <div>
                <div>{editId ? 'Edit Employee' : 'Add Employee'}</div>
                {form.full_name && <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--text2)', marginTop: 2 }}>{form.full_name}</div>}
              </div>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 18, borderBottom: '1px solid var(--border)', paddingBottom: 0, overflowX: 'auto' }}>
              {TABS.map(t => (
                <button key={t} type="button"
                  onClick={() => setTab(t)}
                  style={{
                    padding: '6px 14px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
                    background: 'none', borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent',
                    color: tab === t ? 'var(--accent)' : 'var(--text2)', whiteSpace: 'nowrap',
                    borderRadius: 0,
                  }}>
                  {TAB_LABELS[t]}
                </button>
              ))}
            </div>

            <form onSubmit={e => { e.preventDefault(); saveMutation.mutate(form); }}>

              {/* ── PERSONAL ─────────────────────────────────────── */}
              {tab === 'personal' && (
                <div className="form-grid">
                  <div className="field"><label>Employee Code *</label>
                    <input value={form.employee_code} onChange={e => set('employee_code', e.target.value)} placeholder="EMP001" required />
                  </div>
                  <div className="field"><label>Full Name *</label>
                    <input value={form.full_name} onChange={e => set('full_name', e.target.value)} required />
                  </div>
                  <div className="field"><label>Date of Birth</label>
                    <input type="date" value={form.date_of_birth} onChange={e => set('date_of_birth', e.target.value)} />
                  </div>
                  <div className="field"><label>Gender</label>
                    <select value={form.gender} onChange={e => set('gender', e.target.value)}>
                      <option value="">— Select —</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                      <option value="prefer_not_to_say">Prefer not to say</option>
                    </select>
                  </div>
                  <div className="field"><label>Blood Group</label>
                    <select value={form.blood_group} onChange={e => set('blood_group', e.target.value)}>
                      <option value="">— Select —</option>
                      {BLOOD_GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                  <div className="field"><label>Marital Status</label>
                    <select value={form.marital_status} onChange={e => set('marital_status', e.target.value)}>
                      <option value="">— Select —</option>
                      <option value="single">Single</option>
                      <option value="married">Married</option>
                      <option value="divorced">Divorced</option>
                      <option value="widowed">Widowed</option>
                    </select>
                  </div>
                  <div className="field"><label>Mobile Number</label>
                    <input value={form.mobile_number} onChange={e => set('mobile_number', e.target.value)} placeholder="+91 98765 43210" />
                  </div>
                  <div className="field"><label>Personal Email</label>
                    <input type="email" value={form.personal_email} onChange={e => set('personal_email', e.target.value)} />
                  </div>
                  <div className="field" style={{ gridColumn: '1/-1' }}><label>Company Email</label>
                    <input type="email" value={form.company_email} onChange={e => set('company_email', e.target.value)} />
                  </div>
                  <div style={{ gridColumn: '1/-1', fontSize: 11, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 1, marginTop: 4 }}>Emergency Contact</div>
                  <div className="field"><label>Contact Name</label>
                    <input value={form.emergency_contact_name} onChange={e => set('emergency_contact_name', e.target.value)} />
                  </div>
                  <div className="field"><label>Contact Phone</label>
                    <input value={form.emergency_contact_phone} onChange={e => set('emergency_contact_phone', e.target.value)} />
                  </div>
                  <div className="field"><label>Relationship</label>
                    <input value={form.emergency_contact_relationship} onChange={e => set('emergency_contact_relationship', e.target.value)} placeholder="Spouse, Parent, Sibling…" />
                  </div>
                </div>
              )}

              {/* ── EMPLOYMENT ───────────────────────────────────── */}
              {tab === 'employment' && (
                <div className="form-grid">
                  <div className="field"><label>Department</label>
                    <select value={form.department} onChange={e => { set('department', e.target.value); set('designation', ''); }}>
                      <option value="">— Select Department —</option>
                      {ALL_DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div className="field"><label>Designation</label>
                    <select value={form.designation} onChange={e => set('designation', e.target.value)}>
                      <option value="">— Select Designation —</option>
                      {(DEPT_DESIGNATIONS[form.department] || []).map(d => <option key={d} value={d}>{d}</option>)}
                      {form.designation && !(DEPT_DESIGNATIONS[form.department] || []).includes(form.designation) && (
                        <option value={form.designation}>{form.designation}</option>
                      )}
                    </select>
                  </div>
                  <div className="field"><label>Employment Type</label>
                    <select value={form.employment_type} onChange={e => set('employment_type', e.target.value)}>
                      {Object.entries(EMP_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div className="field"><label>Employment Status</label>
                    <select value={form.employment_status} onChange={e => set('employment_status', e.target.value)}>
                      {Object.entries(EMP_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div className="field"><label>Joining Date</label>
                    <input type="date" value={form.joining_date} onChange={e => set('joining_date', e.target.value)} />
                  </div>
                  <div className="field"><label>Confirmation Date</label>
                    <input type="date" value={form.confirmation_date} onChange={e => set('confirmation_date', e.target.value)} />
                  </div>
                  <div className="field"><label>Exit Date</label>
                    <input type="date" value={form.exit_date} onChange={e => set('exit_date', e.target.value)} />
                  </div>
                  <div className="field"><label>Reporting Manager</label>
                    <input value={form.reporting_manager} onChange={e => set('reporting_manager', e.target.value)} />
                  </div>
                  <div className="field"><label>Work Location</label>
                    <input value={form.work_location} onChange={e => set('work_location', e.target.value)} placeholder="Office / Remote / Hybrid" />
                  </div>
                </div>
              )}

              {/* ── SALARY ───────────────────────────────────────── */}
              {tab === 'salary' && (
                <div className="field">
                  <label>Salary Structure</label>
                  <select value={form.salary_structure_id} onChange={e => set('salary_structure_id', e.target.value)}>
                    <option value="">— Select structure —</option>
                    {salaryStructures.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  {(() => {
                    const ss = salaryStructures.find(s => s.id === form.salary_structure_id);
                    if (!ss) return null;
                    const gross = [ss.basic, ss.hra, ss.conveyance, ss.special_allowance, ss.other_allowance].reduce((a, b) => a + Number(b || 0), 0);
                    return (
                      <div className="card" style={{ marginTop: 12, background: 'var(--accent-light)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, fontSize: 12 }}>
                          {[['Basic', ss.basic], ['HRA', ss.hra], ['Conveyance', ss.conveyance], ['Special Allow.', ss.special_allowance], ['Other Allow.', ss.other_allowance]].map(([l, v]) => (
                            <div key={l}><label style={{ fontSize: 10 }}>{l}</label><div>₹{Number(v||0).toLocaleString('en-IN')}</div></div>
                          ))}
                          <div><label style={{ fontSize: 10 }}>Gross CTC</label><div style={{ fontWeight: 700 }}>₹{gross.toLocaleString('en-IN')}</div></div>
                          <div><label style={{ fontSize: 10 }}>PF %</label><div>{ss.pf_percent}%</div></div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* ── BANK & IDs ───────────────────────────────────── */}
              {tab === 'bank' && (
                <div className="form-grid">
                  <div style={{ gridColumn: '1/-1', fontSize: 11, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 1 }}>Bank Details</div>
                  <div className="field"><label>Account Holder Name</label>
                    <input value={form.account_holder_name} onChange={e => set('account_holder_name', e.target.value)} />
                  </div>
                  <div className="field"><label>Bank Name</label>
                    <input value={form.bank_name} onChange={e => set('bank_name', e.target.value)} />
                  </div>
                  <div className="field"><label>Branch</label>
                    <input value={form.bank_branch} onChange={e => set('bank_branch', e.target.value)} />
                  </div>
                  <div className="field"><label>Account Number</label>
                    <input value={form.account_number} onChange={e => set('account_number', e.target.value)} />
                  </div>
                  <div className="field"><label>IFSC Code</label>
                    <input value={form.ifsc_code} onChange={e => set('ifsc_code', e.target.value)} />
                  </div>
                  <div className="field"><label>UPI ID</label>
                    <input value={form.upi_id} onChange={e => set('upi_id', e.target.value)} placeholder="name@bank" />
                  </div>
                  <div style={{ gridColumn: '1/-1', fontSize: 11, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 1, marginTop: 6 }}>Government IDs</div>
                  <div className="field"><label>PAN Number</label>
                    <input value={form.pan_number} onChange={e => set('pan_number', e.target.value)} placeholder="ABCDE1234F" style={{ textTransform: 'uppercase' }} />
                  </div>
                  <div className="field"><label>Aadhaar Number</label>
                    <input value={form.aadhaar_number} onChange={e => set('aadhaar_number', e.target.value)} placeholder="xxxx xxxx xxxx" />
                  </div>
                  <div className="field"><label>UAN Number</label>
                    <input value={form.uan_number} onChange={e => set('uan_number', e.target.value)} />
                  </div>
                  <div className="field"><label>ESIC Number</label>
                    <input value={form.esic_number} onChange={e => set('esic_number', e.target.value)} />
                  </div>
                  <div className="field"><label>Professional Tax No.</label>
                    <input value={form.professional_tax_number} onChange={e => set('professional_tax_number', e.target.value)} />
                  </div>
                </div>
              )}

              {/* ── DOCUMENTS ────────────────────────────────────── */}
              {tab === 'documents' && (
                <div>
                  {!editId && (
                    <div style={{ padding: '12px 16px', background: 'var(--accent-light)', borderRadius: 8, marginBottom: 16, fontSize: 13, color: 'var(--text2)' }}>
                      Save the employee first, then come back to upload documents.
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    {[
                      { label: 'Aadhaar Card', key: 'aadhaar' },
                      { label: 'PAN Card', key: 'pan' },
                      { label: 'Driving License', key: 'driving_license' },
                    ].map(({ label, key }) => {
                      const dbKey = key === 'aadhaar' ? 'aadhaar_doc' : key === 'pan' ? 'pan_doc' : 'driving_license_doc';
                      return (
                        <DocUploadBox
                          key={key}
                          label={label}
                          fieldKey={key}
                          empId={editId}
                          currentFile={docFiles[dbKey]}
                          disabled={!editId}
                          onUploaded={(url, filename) => setDocFiles(d => ({ ...d, [dbKey]: filename }))}
                          onDeleted={() => setDocFiles(d => ({ ...d, [dbKey]: null }))}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="modal-actions" style={{ marginTop: 20 }}>
                <button type="button" onClick={() => setShowModal(false)}>Close</button>
                {tab !== 'documents' && (
                  <button type="submit" className="btn-primary" disabled={saveMutation.isPending}>
                    {saveMutation.isPending ? 'Saving…' : editId ? 'Save Changes' : 'Add Employee'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Grant access modal */}
      {grantEmployee && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 380 }}>
            <div className="modal-title">Grant app access — {grantEmployee.full_name}</div>
            <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 14 }}>
              Creates a login account for <strong>{grantEmployee.full_name}</strong> to access 8D Business OS™.
            </p>
            <div className="field"><label>Username (login ID)</label>
              <input value={grantForm.username} onChange={e => setGrantForm(f => ({ ...f, username: e.target.value }))} placeholder="e.g. priya" autoFocus />
            </div>
            <div className="field"><label>Role</label>
              <select value={grantForm.role} onChange={e => setGrantForm(f => ({ ...f, role: e.target.value }))}>
                <option value="team">Team — can log work, view own entries</option>
                <option value="admin">Admin — HR access + manage entries</option>
                <option value="accountant">Accountant — invoices &amp; finance only</option>
              </select>
            </div>
            <div className="field"><label>Temporary password (min 6 chars)</label>
              <input type="text" value={grantForm.password} onChange={e => setGrantForm(f => ({ ...f, password: e.target.value }))} placeholder="Share with them to log in" />
            </div>
            <div className="modal-actions">
              <button onClick={() => setGrantEmployee(null)}>Cancel</button>
              <button className="btn-primary" disabled={grantMutation.isPending} onClick={() => {
                if (!grantForm.username || !grantForm.password) { toast.error('Fill all fields'); return; }
                if (grantForm.password.length < 6) { toast.error('Password min 6 chars'); return; }
                grantMutation.mutate({ empId: grantEmployee.id, ...grantForm });
              }}>
                {grantMutation.isPending ? 'Granting…' : 'Grant Access'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!revokeEmployee}
        title={`Revoke access for ${revokeEmployee?.full_name}?`}
        message={`${revokeEmployee?.full_name} will no longer be able to log in. Their employee record stays intact.`}
        confirmLabel="Revoke Access"
        onConfirm={() => revokeMutation.mutate(revokeEmployee.id)}
        onCancel={() => setRevokeEmployee(null)}
      />
      <ConfirmDialog
        open={!!deactivateEmployee}
        title={`Deactivate ${deactivateEmployee?.full_name}?`}
        message="This will also revoke their app login if they have one. Historical data is preserved."
        confirmLabel="Deactivate"
        onConfirm={() => deactivateMutation.mutate(deactivateEmployee.id)}
        onCancel={() => setDeactivateEmployee(null)}
      />
    </div>
  );
}
